import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Envelope, Plus, X, Wrench, LockKey, LockKeyOpen, Warning, ArrowClockwise, CheckCircle, Broom } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function HRRecipients() {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  // Admin controls state
  const [teamPolicy, setTeamPolicy] = useState("release"); // release | lock
  const [policySaving, setPolicySaving] = useState(false);
  const [renumberBusy, setRenumberBusy] = useState(false);
  const [renumberPreview, setRenumberPreview] = useState(null); // {would_update, would_skip, plan}
  const [renumberResult, setRenumberResult] = useState(null);   // {applied, plan}
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Toplu Ekip Silme (Feb 2026)
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null);  // {would_delete, would_skip, plan}
  const [purgeResult, setPurgeResult] = useState(null);    // {deleted, skipped, skipped_teams}
  const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/hr-report-recipients`);
      setRecipients(data.recipients || []);
    } catch (e) { toast.error(formatErr(e)); }
    try {
      const { data } = await axios.get(`${API}/teams/code-policy`);
      setTeamPolicy(data.policy || "release");
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const togglePolicy = async () => {
    const next = teamPolicy === "release" ? "lock" : "release";
    setPolicySaving(true);
    try {
      const { data } = await axios.put(`${API}/teams/code-policy`, { policy: next });
      setTeamPolicy(data.policy);
      toast.success(next === "lock"
        ? "Silinen ekip kodları artık kilitli tutuluyor (yeniden kullanılmayacak)."
        : "Silinen ekip kodları serbest bırakıldı (yeni ekiplere atanabilir)."
      );
    } catch (e) { toast.error(formatErr(e)); }
    setPolicySaving(false);
  };

  const previewRenumber = async () => {
    setRenumberBusy(true);
    try {
      const { data } = await axios.post(`${API}/employees/renumber?dry_run=true`);
      setRenumberPreview(data);
      setRenumberResult(null);
      setConfirmOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
    setRenumberBusy(false);
  };

  const applyRenumber = async () => {
    setRenumberBusy(true);
    try {
      const { data } = await axios.post(`${API}/employees/renumber`);
      setRenumberResult(data);
      toast.success(`${data.applied} personel kodu yeni şemaya taşındı.`);
    } catch (e) { toast.error(formatErr(e)); }
    setRenumberBusy(false);
    setConfirmOpen(false);
  };

  const previewPurge = async () => {
    setPurgeBusy(true);
    try {
      const { data } = await axios.post(`${API}/teams/purge-seeded`, { dry_run: true });
      setPurgePreview(data);
      setPurgeResult(null);
      setPurgeConfirmOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
    setPurgeBusy(false);
  };

  const applyPurge = async () => {
    setPurgeBusy(true);
    try {
      const { data } = await axios.post(`${API}/teams/purge-seeded`, {});
      setPurgeResult(data);
      toast.success(
        `${data.deleted} varsayılan ekip silindi.`
        + (data.skipped > 0 ? ` (${data.skipped} ekip personel bağı olduğu için atlandı)` : "")
      );
    } catch (e) { toast.error(formatErr(e)); }
    setPurgeBusy(false);
    setPurgeConfirmOpen(false);
  };

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/hr-report-recipients`,
        { recipients: next });
      setRecipients(data.recipients || []);
      toast.success("Alıcı listesi güncellendi");
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  const add = async () => {
    const em = (newEmail || "").trim().toLowerCase();
    if (!em) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      toast.error("Geçerli bir e-posta adresi girin");
      return;
    }
    if (recipients.includes(em)) {
      toast.error("Bu adres zaten listede");
      return;
    }
    await save([...recipients, em]);
    setNewEmail("");
  };

  const remove = async (em) => {
    await save(recipients.filter((r) => r !== em));
  };

  const isAdmin = user?.role === "yonetici";

  return (
    <Shell title="İK Ayarları">
      <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
        <div className="flex items-start gap-3">
          <Envelope size={24} weight="duotone" className="text-[#1B4776] mt-1" />
          <div className="flex-1">
            <div className="text-base font-bold font-heading">Günlük Personel Raporu Alıcıları</div>
            <div className="text-xs text-slate-600 mt-1">
              Her gün <b>İstanbul saatiyle 23:59'da</b> Personel Yönetimi Raporu PDF'i bu adreslere otomatik olarak e-posta ile gönderilir.
              Sadece <b>yönetici</b> rolündeki kullanıcılar bu listeyi düzenleyebilir.
            </div>
          </div>
        </div>
      </Card>

      {/* Adres ekleme */}
      {isAdmin && (
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
            Yeni Alıcı E-postası
          </Label>
          <div className="flex gap-2 mt-1">
            <Input value={newEmail}
                   data-testid="hr-recipient-input"
                   onChange={(e) => setNewEmail(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                   placeholder="ornek@sirket.com"
                   className="rounded-none sm:rounded-sm flex-1" />
            <Button onClick={add} disabled={saving} data-testid="hr-recipient-add"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <Plus size={14} className="mr-2" /> Ekle
            </Button>
          </div>
        </Card>
      )}

      {/* Liste */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm overflow-hidden">
        {recipients.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <Envelope size={32} weight="duotone" className="text-slate-400 mx-auto mb-2" />
            <div>Henüz alıcı eklenmemiş. Otomatik e-posta gönderimi devre dışıdır.</div>
          </div>
        ) : (
          <ul>
            {recipients.map((em, i) => (
              <li key={em}
                  data-testid={`hr-recipient-row-${i}`}
                  className={`flex items-center justify-between px-4 py-3 ${i % 2 ? "bg-slate-50" : ""}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1B4776] text-white flex items-center justify-center text-xs font-bold">
                    {em.slice(0, 2).toUpperCase()}
                  </div>
                  <span className="font-mono text-sm">{em}</span>
                </div>
                {isAdmin && (
                  <button onClick={() => remove(em)}
                          data-testid={`hr-recipient-remove-${i}`}
                          className="w-7 h-7 inline-flex items-center justify-center hover:bg-red-50">
                    <X size={13} className="text-red-600" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ---------------- Admin: Team Code Policy + Renumber ---------------- */}
      {isAdmin && (
        <>
          <div className="mt-8 mb-3 flex items-center gap-2">
            <Wrench size={16} weight="duotone" className="text-[#1B4776]" />
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-700">
              Yönetici Araçları — Personel Kod Şeması
            </div>
          </div>

          {/* Team code policy */}
          <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
            <div className="flex items-start gap-3">
              {teamPolicy === "lock"
                ? <LockKey size={22} weight="duotone" className="text-purple-700 mt-1" />
                : <LockKeyOpen size={22} weight="duotone" className="text-emerald-700 mt-1" />}
              <div className="flex-1">
                <div className="text-sm font-bold font-heading">Silinen Ekip Kodu Politikası</div>
                <div className="text-xs text-slate-600 mt-1">
                  {teamPolicy === "lock" ? (
                    <>Şu an <b>Kilitli</b>: silinen ekip kodları bloke — yeni ekiplere <b>tekrar atanmaz</b>.
                      Aynı kodun geçmiş kayıtlarda farklı bir ekibi göstermesi engellenir.</>
                  ) : (
                    <>Şu an <b>Serbest</b>: silinen ekip kodları serbest bırakılır — sıradaki yeni ekip
                      boşta kalan en küçük kodu (ör. <span className="font-mono">04</span>) alır.</>
                  )}
                </div>
                <div className="mt-3">
                  <Button
                    data-testid="team-code-policy-toggle"
                    onClick={togglePolicy}
                    disabled={policySaving}
                    className={`rounded-none sm:rounded-sm text-white ${
                      teamPolicy === "lock"
                        ? "bg-emerald-700 hover:bg-emerald-800"
                        : "bg-purple-700 hover:bg-purple-800"
                    }`}
                  >
                    {teamPolicy === "lock"
                      ? <><LockKeyOpen size={14} className="mr-2" /> Serbest Bırak</>
                      : <><LockKey size={14} className="mr-2" /> Kilitle</>}
                  </Button>
                  <span className="ml-3 text-[10px] font-mono uppercase text-slate-500">
                    Aktif politika: {teamPolicy}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Renumber employees */}
          <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
            <div className="flex items-start gap-3">
              <ArrowClockwise size={22} weight="duotone" className="text-[#1B4776] mt-1" />
              <div className="flex-1">
                <div className="text-sm font-bold font-heading">Toplu Kod Yeniden Numaralandırma</div>
                <div className="text-xs text-slate-600 mt-1">
                  Eski <span className="font-mono">EMP-NNNN</span> formatındaki personel kodlarını
                  yeni <span className="font-mono">&#123;PROJE&#125;-&#123;EKIP&#125;-NNN</span> şemasına
                  (örn: <span className="font-mono">A18-04-001</span>) taşır.
                  Şantiye veya ekibi olmayan kayıtlar atlanır. Bu işlem geri alınamaz — önce önizleme gösterilir.
                </div>
                {renumberResult && (
                  <div className="mt-3 border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2"
                       data-testid="renumber-result">
                    <CheckCircle size={14} weight="fill" className="text-emerald-700" />
                    <span><b>{renumberResult.applied}</b> personel yeni şemaya taşındı.</span>
                  </div>
                )}
                <div className="mt-3">
                  <Button
                    data-testid="renumber-preview-btn"
                    onClick={previewRenumber}
                    disabled={renumberBusy}
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white"
                  >
                    <ArrowClockwise size={14} className="mr-2" />
                    Önizle ve Uygula
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Purge Seeded Teams */}
          <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
            <div className="flex items-start gap-3">
              <Broom size={22} weight="duotone" className="text-red-700 mt-1" />
              <div className="flex-1">
                <div className="text-sm font-bold font-heading">Varsayılan Ekipleri Toplu Sil</div>
                <div className="text-xs text-slate-600 mt-1">
                  Sistem tarafından otomatik oluşturulmuş varsayılan ekipleri (Duvar Ekibi, Boya Ekibi,
                  Alçı Sıva Ekibi vb.) tek tıkla siler. <b>Personeli olan ekipler otomatik olarak atlanır</b>.
                  Bu işlem geri alınamaz — önce önizleme gösterilir.
                </div>
                {purgeResult && (
                  <div className="mt-3 border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2"
                       data-testid="purge-result">
                    <CheckCircle size={14} weight="fill" className="text-emerald-700" />
                    <span><b>{purgeResult.deleted}</b> varsayılan ekip silindi.
                    {purgeResult.skipped > 0 && (
                      <span className="text-amber-800 ml-2">
                        ({purgeResult.skipped} ekip personel bağı olduğu için atlandı)
                      </span>
                    )}</span>
                  </div>
                )}
                <div className="mt-3">
                  <Button
                    data-testid="purge-preview-btn"
                    onClick={previewPurge}
                    disabled={purgeBusy}
                    className="rounded-none sm:rounded-sm bg-red-700 hover:bg-red-800 text-white"
                  >
                    <Broom size={14} className="mr-2" />
                    Önizle ve Sil
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <AlertDialog open={purgeConfirmOpen} onOpenChange={setPurgeConfirmOpen}>
            <AlertDialogContent className="rounded-none sm:rounded-sm">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Warning size={20} weight="duotone" className="text-red-600" />
                  Varsayılan Ekipleri Silme Önizlemesi
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="text-xs text-slate-700 space-y-2 mt-2">
                    <div>Aşağıdaki değişiklikler <b>uygulanacak</b>:</div>
                    <div className="flex gap-4 font-mono text-xs">
                      <span><b>{purgePreview?.would_delete ?? 0}</b> silinecek</span>
                      <span className="text-amber-700"><b>{purgePreview?.would_skip ?? 0}</b> atlanacak</span>
                    </div>
                    <div className="max-h-56 overflow-auto border border-slate-200 bg-slate-50">
                      <table className="w-full text-[11px] font-mono">
                        <thead className="bg-slate-100 text-slate-600 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1">Kod</th>
                            <th className="text-left px-2 py-1">Ekip Adı</th>
                            <th className="text-right px-2 py-1">Personel</th>
                            <th className="text-left px-2 py-1">Durum</th>
                          </tr>
                        </thead>
                        <tbody data-testid="purge-preview-table">
                          {(purgePreview?.plan || []).map((p) => (
                            <tr key={p.id} className="border-t border-slate-200">
                              <td className="px-2 py-0.5">{p.code || "—"}</td>
                              <td className="px-2 py-0.5">{p.name}</td>
                              <td className="px-2 py-0.5 text-right">{p.member_count}</td>
                              <td className={`px-2 py-0.5 ${p.reason ? "text-amber-700" : "text-red-700"}`}>
                                {p.reason || "Silinecek"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-[11px] text-red-700">
                      Bu işlem <b>geri alınamaz</b>. Onayladığınızda kayıtlar hemen silinir (soft-delete).
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="purge-cancel"
                                   className="rounded-none sm:rounded-sm">
                  Vazgeç
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="purge-apply-btn"
                  onClick={applyPurge}
                  disabled={purgeBusy || !(purgePreview?.would_delete > 0)}
                  className="rounded-none sm:rounded-sm bg-red-700 hover:bg-red-800 text-white"
                >
                  Onayla ve Sil
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent className="rounded-none sm:rounded-sm">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Warning size={20} weight="duotone" className="text-amber-600" />
                  Toplu Yeniden Numaralandırma Önizlemesi
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="text-xs text-slate-700 space-y-2 mt-2">
                    <div>
                      Aşağıdaki değişiklikler <b>uygulanacak</b>:
                    </div>
                    <div className="flex gap-4 font-mono text-xs">
                      <span><b>{renumberPreview?.would_update ?? 0}</b> güncellenecek</span>
                      <span className="text-slate-500"><b>{renumberPreview?.would_skip ?? 0}</b> atlanacak</span>
                    </div>
                    <div className="max-h-56 overflow-auto border border-slate-200 bg-slate-50">
                      <table className="w-full text-[11px] font-mono">
                        <thead className="bg-slate-100 text-slate-600 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1">Eski Kod</th>
                            <th className="text-left px-2 py-1">Yeni Kod</th>
                            <th className="text-left px-2 py-1">Not</th>
                          </tr>
                        </thead>
                        <tbody data-testid="renumber-preview-table">
                          {(renumberPreview?.plan || []).map((p) => (
                            <tr key={p.id} className="border-t border-slate-200">
                              <td className="px-2 py-0.5">{p.old_code}</td>
                              <td className="px-2 py-0.5 text-emerald-700">
                                {p.new_code || <span className="text-slate-400">—</span>}
                              </td>
                              <td className="px-2 py-0.5 text-red-600 lowercase">{p.skipped || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-[11px] text-red-700">
                      Bu işlem <b>geri alınamaz</b>. Onayladığınızda kayıtlar hemen güncellenir.
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="renumber-cancel"
                                   className="rounded-none sm:rounded-sm">
                  Vazgeç
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="renumber-apply-btn"
                  onClick={applyRenumber}
                  disabled={renumberBusy || !(renumberPreview?.would_update > 0)}
                  className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white"
                >
                  Onayla ve Uygula
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </Shell>
  );
}
